import React from 'react'
import type { FfpDashboardData } from '../../types'
import { longMonth, monthOf } from '../../utils'
import { MEASURE_KEYS, MEASURES, RETIREMENT } from '../../copy'
import { Reveal } from '../Reveal'
import styles from './earlyDays.module.sass'

// The design's Early days stage, without its timeline dates and joining-link
// button: no programme dates are stored yet. The server sends health and
// progress empty in early days, so nothing here reads them.
export const HealthEarlyDays = ({ data }: { data: FfpDashboardData }) => {
  const { people } = data
  const cards = [
    { name: RETIREMENT.name, meaning: RETIREMENT.meaning },
    ...MEASURE_KEYS.map((key) => ({ name: MEASURES[key].name, meaning: MEASURES[key].about })),
  ].map((card) => <PendingCard key={card.name} name={card.name} meaning={card.meaning} data={data} />)

  return (
    <div className={styles.layout}>
      <Reveal>
        <article className={styles.next}>
          <h3 className={styles.nextTitle}>What happens next</h3>
          <ol className={styles.steps}>
            <Step title={`${people.onboarded} of ${people.enrolled} enrolled people have onboarded`} detail={null} now />
            <Step title="First health results" detail={`Once at least ${people.early_days_threshold} people have onboarded.`} />
            <Step title="First trends and progress" detail="After two months of data, you’ll see who is moving." />
          </ol>
          <p className={styles.nudge}>
            The quickest way to get results sooner is more people onboarding. A reminder from you in your next team update helps.
          </p>
        </article>
      </Reveal>
      <div className={styles.pending}>{cards}</div>
    </div>
  )
}

const Step = ({ title, detail, now = false }: { title: string; detail: string | null; now?: boolean }) => (
  <li className={styles.step}>
    <span className={now ? `${styles.dot} ${styles.dotNow}` : styles.dot} aria-hidden="true" />
    <div className={styles.stepText}>
      <span className={styles.stepTitle}>{title}</span>
      <StepDetail text={detail} />
    </div>
  </li>
)

const StepDetail = ({ text }: { text: string | null }) => {
  if (!text) return null

  return <span className={styles.stepDetail}>{text}</span>
}

type PendingProps = {
  name: string
  meaning: string
  data: FfpDashboardData
}

// A measure that can't be shown yet, and how close it is.
const PendingCard = ({ name, meaning, data }: PendingProps) => {
  const { onboarded, early_days_threshold: needed } = data.people
  const progress = needed > 0 ? Math.min(100, Math.round((onboarded / needed) * 100)) : 100
  const captured = monthOf(data.freshness.captured_at)
  const footer = [data.organization.name, captured ? longMonth(captured) : null, `${onboarded} onboarded`].filter(Boolean).join(' · ')

  return (
    <Reveal>
      <article className={styles.card}>
        <h3 className={styles.cardName}>{name}</h3>
        <p className={styles.cardMeaning}>{meaning}</p>
        <div className={styles.box}>
          <p className={styles.boxTitle}>
            Not enough data yet: {onboarded} of the {needed} people needed
          </p>
          <div className={styles.bar} aria-hidden="true">
            <span className={styles.barFill} style={{ width: `${progress}%` }} />
          </div>
          <p className={styles.boxDetail}>First results show once at least {needed} people have onboarded.</p>
        </div>
        <p className={styles.cardFooter}>{footer}</p>
      </article>
    </Reveal>
  )
}

// Progress waits for early days to end and for 5 people with two months of
// data, so no count can point at one person.
export const ProgressEarlyDays = ({ threshold }: { threshold: number }) => (
  <div className={styles.waiting}>
    <h3 className={styles.waitingTitle}>Progress shows once enough people have two months of data</h3>
    <p className={styles.waitingText}>
      {`To show who has moved up or down, we compare each person’s latest month with their first. That needs ${threshold} people onboarded, and 5 of them with two months of data.`}
    </p>
  </div>
)
